import { useEffect, useRef, useState } from "react";
import { MailIcon } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

import AuthModal from "../components/AuthModal";
import Loader from "../components/Loader";
import Modal from "../components/Modal";
import PublicNav from "../components/PublicNav";
import ContactModal from "../components/ui/ContactModal";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";

function IconChat() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );
}

function IconCode() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="16 18 22 12 16 6" />
      <polyline points="8 6 2 12 8 18" />
    </svg>
  );
}

function IconBook() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
    </svg>
  );
}

function IconTrace() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.35-4.35" />
      <path d="M11 8v3l2 2" />
    </svg>
  );
}

function IconFlask() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 3h6M9 3v8L4.5 18.5A1 1 0 0 0 5.4 20h13.2a1 1 0 0 0 .9-1.5L15 11V3" />
      <path d="M6.5 17h11" />
    </svg>
  );
}

function IconShield() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <path d="m9 12 2 2 4-4" />
    </svg>
  );
}

function IconArrow() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function AppPreviewMock() {
  const messages = [
    { role: "user", text: "We're preparing a SOX audit. What SAP access risks should I prioritize?" },
    { role: "ai", text: "Based on your project's ECC landscape, the top priorities are unrestricted SE38/SE80 (custom code execution), open SM30 table maintenance, and 3 active CVEs in your basis stack that haven't been patched. I can generate a remediation checklist aligned to your role structure." },
    { role: "user", text: "Great. Can you analyze last month's ST01 traces and design least-privilege roles?" },
  ];

  return (
    <div className="lp-app-mock">
      <div className="lp-app-mock-bar">
        <div className="lp-app-mock-dots">
          <span /><span /><span />
        </div>
        <span className="lp-app-mock-title">tozenAI — SAP Security Assistant</span>
      </div>
      <div className="lp-app-mock-body">
        {messages.map((msg, i) => (
          <div key={i} className={`lp-app-mock-msg lp-app-mock-msg--${msg.role}`}>
            {msg.role === "ai" && (
              <div className="lp-app-mock-avatar">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>
              </div>
            )}
            <div className="lp-app-mock-bubble">{msg.text}</div>
          </div>
        ))}
        <div className="lp-app-mock-typing">
          <span /><span /><span />
        </div>
      </div>
    </div>
  );
}

const FEATURES = [
  {
    icon: <IconChat />,
    tag: "Assistant",
    title: "AI Security Assistant",
    desc: "Chat with an AI trained on SAP Security Notes, GRC guides, and hardening frameworks. Diagnose access issues, design roles, and get compliance guidance in plain language.",
    badge: null,
  },
  {
    icon: <IconTrace />,
    tag: "Agent · Pro",
    title: "Authorization Trace Analyzer",
    desc: "Upload ST01 or STAUTHTRACE exports and get AI-designed single roles, composite roles, and naming convention-aligned build profiles — in minutes, not days.",
    badge: "Pro",
  },
  {
    icon: <IconCode />,
    tag: "Developer",
    title: "Developer Tools",
    desc: "Generate and analyze ABAP, SQLScript, and Python code for security automation. Every snippet reviewed against SAP security standards and your project context.",
    badge: null,
  },
  {
    icon: <IconShield />,
    tag: "Intelligence",
    title: "CVE Library",
    desc: "Track SAP security vulnerabilities across your landscape. Review, whitelist, and manage CVE status with full history — filtered to what matters for your systems.",
    badge: null,
  },
  {
    icon: <IconBook />,
    tag: "Knowledge",
    title: "Knowledge Base",
    desc: "Structured access to SAP Security Notes, official GRC guides, and your own uploaded documents — all searchable and cited in AI responses.",
    badge: null,
  },
];

const STEPS = [
  {
    num: "01",
    title: "Register your SAP landscape",
    desc: "Create a project for each system — define modules, org units, and role patterns. tozenAI uses this context to give you precise, not generic, answers.",
  },
  {
    num: "02",
    title: "Ask, analyze, or automate",
    desc: "Chat for guidance, upload authorization traces for role design, or kick off UAT runs — all within a single platform built for SAP security.",
  },
  {
    num: "03",
    title: "Ship audit-ready deliverables",
    desc: "Export designed roles, generated code, CVE remediation plans, and UAT evidence packages directly from the platform.",
  },
];

export default function HomePage() {
  const navigate = useNavigate();
  const { isAuthenticated, bootstrapping } = useAuth();
  const { isDark } = useTheme();
  const [loginOpen, setLoginOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const featuresRef = useRef(null);
  const licensingRef = useRef(null);

  const openLogin = () => setLoginOpen(true);
  const openContact = () => setContactOpen(true);
  const scrollTo = (ref) => ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  const navLinks = [
    { key: "features", label: "Features", onClick: () => scrollTo(featuresRef) },
    { key: "licensing", label: "Licensing", onClick: () => scrollTo(licensingRef) },
    { key: "blog", label: "Blog", to: "/blog" },
    { key: "contact", label: "Contact", onClick: openContact },
  ];

  useEffect(() => {
    if (bootstrapping || !isAuthenticated) return;
    navigate("/home", { replace: true });
  }, [bootstrapping, isAuthenticated, navigate]);

  useEffect(() => {
    const html = document.documentElement;
    const root = document.getElementById("root");
    html.style.overflowY = "auto";
    if (root) root.style.overflowY = "auto";
    return () => {
      html.style.overflowY = "";
      if (root) root.style.overflowY = "";
    };
  }, []);

  if (bootstrapping || isAuthenticated) return <Loader />;

  return (
    <div className="lp-root">

      {/* ── Navigation ──────────────────────────────── */}
      <PublicNav links={navLinks} onLogin={openLogin} onContact={openContact} />

      {/* ── Hero ────────────────────────────────────── */}
      <section className="lp-hero">
        <div className="lp-hero-bg" aria-hidden="true" />
        <div className="lp-hero-content">
          <div className="lp-hero-badge">SAP Security Platform</div>
          <h1 className="lp-hero-heading">
            The AI platform built<br />for SAP Security
          </h1>
          <p className="lp-hero-sub">
            From authorization trace analysis to UAT evidence — tozenAI covers the full SAP security lifecycle. AI guidance, automated role design, CVE tracking, and audit-ready deliverables in one platform.
          </p>
          <div className="lp-hero-ctas">
            <button type="button" className="lp-btn-primary" onClick={openContact}>
              Contact us <IconArrow />
            </button>
            <button type="button" className="lp-btn-ghost" onClick={openLogin}>
              Sign in
            </button>
          </div>
          <a
            href="https://www.producthunt.com/products/tozenlabs?embed=true&utm_source=badge-featured&utm_medium=badge&utm_campaign=badge-tozenlabs"
            target="_blank"
            rel="noopener noreferrer"
            className="lp-ph-badge"
          >
            <img
              alt="tozenLabs - SAP AI Companion for Security & Basis consultant/freelancers | Product Hunt"
              width="250"
              height="54"
              src={
                isDark
                  ? "https://api.producthunt.com/widgets/embed-image/v1/featured.svg?post_id=1138103&theme=dark&t=1777823818870"
                  : "https://api.producthunt.com/widgets/embed-image/v1/featured.svg?post_id=1138103&theme=light&t=1777823760234"
              }
            />
          </a>
        </div>
        <div className="lp-hero-preview">
          <div className="lp-preview-glow" aria-hidden="true" />
          <div className="lp-preview-frame">
            <AppPreviewMock />
          </div>
        </div>
      </section>

      {/* ── Features ────────────────────────────────── */}
      <section className="lp-features" ref={featuresRef} id="features">
        <div className="lp-section-inner">
          <div className="lp-section-header">
            <p className="lp-section-eyebrow">Platform capabilities</p>
            <h2 className="lp-section-title">The full SAP security stack, powered by AI</h2>
            <p className="lp-section-sub">
              Five integrated tools — from conversational guidance to automated role design and CVE tracking.
            </p>
          </div>
          <div className="lp-features-grid">
            {FEATURES.map((f) => (
              <div key={f.title} className="lp-feature-card">
                {f.badge && <span className="lp-feature-pro-badge">{f.badge}</span>}
                <div className="lp-feature-icon">{f.icon}</div>
                <div className="lp-feature-tag">{f.tag}</div>
                <h3 className="lp-feature-title">{f.title}</h3>
                <p className="lp-feature-desc">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How it works ────────────────────────────── */}
      <section className="lp-how">
        <div className="lp-section-inner">
          <div className="lp-section-header">
            <p className="lp-section-eyebrow">How it works</p>
            <h2 className="lp-section-title">From system context to audit deliverable</h2>
          </div>
          <div className="lp-steps">
            {STEPS.map((step, i) => (
              <div key={step.num} className="lp-step">
                <div className="lp-step-num">{step.num}</div>
                {i < STEPS.length - 1 && (
                  <div className="lp-step-arrow" aria-hidden="true">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path d="M5 12h14M15 8l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                )}
                <h3 className="lp-step-title">{step.title}</h3>
                <p className="lp-step-desc">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Licensing ───────────────────────────────── */}
      <section className="lp-licensing" ref={licensingRef} id="licensing">
        <div className="lp-section-inner">
          <div className="lp-section-header">
            <p className="lp-section-eyebrow">Licensing</p>
            <h2 className="lp-section-title">Licensed per organization</h2>
            <p className="lp-section-sub">
              tozenAI is available by request. We tailor access to your organization&rsquo;s SAP
              landscape and team size — reach out and we&rsquo;ll set up your account.
            </p>
          </div>
          <div className="lp-licensing-card">
            <div className="lp-licensing-icon">
              <MailIcon size={22} strokeWidth={1.5} />
            </div>
            <p className="lp-licensing-desc">
              Email us at{" "}
              <a href="mailto:fgandolfo@tozenlabs.com" className="lp-licensing-mail">
                fgandolfo@tozenlabs.com
              </a>{" "}
              or send a message below to get access. We&rsquo;ll create your organization and send
              your team an invite link.
            </p>
            <button type="button" className="lp-btn-primary lp-licensing-cta" onClick={openContact}>
              Contact us <IconArrow />
            </button>
          </div>
        </div>
      </section>

      {/* ── Final CTA ───────────────────────────────── */}
      <section className="lp-cta-section">
        <div className="lp-cta-glow" aria-hidden="true" />
        <div className="lp-section-inner lp-cta-inner">
          <h2 className="lp-cta-heading">Your entire SAP security workflow, in one place</h2>
          <p className="lp-cta-sub">
            Join SAP security consultants and Basis teams using tozenAI to design roles, track vulnerabilities, and deliver audit evidence faster.
          </p>
          <button type="button" className="lp-btn-primary lp-cta-btn" onClick={openContact}>
            Contact us <IconArrow />
          </button>
        </div>
      </section>

      {/* ── Footer ──────────────────────────────────── */}
      <footer className="lp-footer">
        <div className="lp-footer-inner">
          <div className="lp-footer-brand">
            <div className="lp-footer-wordmark">
              <span className="lp-brand-tozen">tozen</span><span className="lp-brand-ai">AI</span>
            </div>
            <p className="lp-footer-tagline">SAP Security Intelligence</p>
          </div>
          <nav className="lp-footer-links" aria-label="Footer navigation">
            <button type="button" className="lp-footer-link" onClick={() => scrollTo(featuresRef)}>Features</button>
            <button type="button" className="lp-footer-link" onClick={() => scrollTo(licensingRef)}>Licensing</button>
            <Link to="/blog" className="lp-footer-link">Blog</Link>
            <button type="button" className="lp-footer-link" onClick={openContact}>Contact</button>
          </nav>
          <p className="lp-footer-copy">© {new Date().getFullYear()} tozenLabs. All rights reserved.</p>
        </div>
      </footer>

      {/* ── Modals ──────────────────────────────────── */}
      <AuthModal isOpen={loginOpen} onClose={() => setLoginOpen(false)} />
      <Modal
        isOpen={contactOpen}
        onClose={() => setContactOpen(false)}
        title={null}
        wide
      >
        <ContactModal />
      </Modal>
    </div>
  );
}
